import { daysBetween, shiftDayKey, type DayKey } from '../lib/time'

/**
 * Anything you want to keep a count of alongside the work: cups of coffee, days
 * you walked in, evenings you left early. Deliberately not a habit tracker —
 * there is no notion of success, only of what happened and when.
 *
 * A tracker is either a `count` (how many, each time) or a `check` (it happened
 * today, or it did not). Both are stored the same way; the kind only decides
 * what the buttons do and how a day is read back.
 */
export interface Tracker {
  id: string
  label: string
  kind: 'count' | 'check'
  /** What one counts, e.g. "cup". Null for a check, or when a bare number reads fine. */
  unit: string | null
  color: string
  /** Retired: kept for its history, off the logging row. */
  archived: boolean
}

/** `users/{uid}/trackerDays/{trackerId}__{day}` */
export interface TrackerDay {
  trackerId: string
  day: DayKey
  /** How many times it happened. A check is 0 or 1. */
  count: number
  /** When each one was logged, so a day can say more than a number. */
  times: number[]
}

export const TRACKER_COLORS = [
  '#6aa5ff',
  '#3ecfa0',
  '#7ed957',
  '#f0a92e',
  '#b48bf2',
  '#ef7fc6',
  '#f2605f',
  '#8b93a1',
]

export function newTrackerId(label: string, taken: Set<string>): string {
  const base =
    label
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '')
      .slice(0, 24) || 'tracker'
  if (!taken.has(base)) return base
  for (let n = 2; ; n++) {
    const candidate = `${base}-${n}`
    if (!taken.has(candidate)) return candidate
  }
}

/**
 * The document id. Both parts are needed to read a day back without a compound
 * index, and a double underscore keeps them separable — tracker ids never
 * contain one, because they are slugged with single hyphens.
 */
export function trackerDayId(trackerId: string, day: DayKey): string {
  return `${trackerId}__${day}`
}

export function normaliseTrackers(raw: unknown): Tracker[] {
  const list = Array.isArray((raw as { trackers?: unknown })?.trackers)
    ? ((raw as { trackers: unknown[] }).trackers)
    : Array.isArray(raw)
      ? (raw as unknown[])
      : []
  return list
    .filter((entry): entry is Partial<Tracker> => Boolean(entry) && typeof entry === 'object')
    .filter((entry) => typeof entry.id === 'string' && entry.id.length > 0)
    .map((entry, index) => ({
      id: entry.id as string,
      label: entry.label || (entry.id as string),
      kind: entry.kind === 'check' ? 'check' : 'count',
      unit: entry.unit ? entry.unit : null,
      color: entry.color || (TRACKER_COLORS[index % TRACKER_COLORS.length] as string),
      archived: entry.archived === true,
    }))
}

export function normaliseTrackerDay(raw: unknown): TrackerDay | null {
  const entry = raw as Partial<TrackerDay> | null
  if (!entry || typeof entry.trackerId !== 'string' || typeof entry.day !== 'string') return null
  const times = Array.isArray(entry.times) ? entry.times.filter((t) => typeof t === 'number') : []
  const count = typeof entry.count === 'number' && Number.isFinite(entry.count)
    ? Math.max(0, Math.round(entry.count))
    : times.length
  return { trackerId: entry.trackerId, day: entry.day, count, times }
}

/** Why a tracker list is not ready to be written, or null when it is. */
export function trackersProblem(trackers: Tracker[]): string | null {
  if (trackers.some((tracker) => !tracker.label.trim())) return 'Every tracker needs a name.'
  return null
}

export interface TrackerStats {
  total: number
  /** Days with at least one entry. */
  days: number
  /** Consecutive days with an entry, counting back from `today`. */
  streak: number
  longestStreak: number
  /** Mean per day across the days in range, from the first entry onwards. */
  perDay: number
  busiest: { day: DayKey; count: number } | null
}

/**
 * The overall read, which is the point of the whole thing: not "logged today"
 * but when it happened, how often, and whether that is changing.
 *
 * Averages start from the first day anything was logged, for the same reason
 * tracking coverage does: a tracker made yesterday has not missed the year
 * before it existed.
 */
export function trackerStats(days: Map<DayKey, number>, today: DayKey): TrackerStats {
  const logged = [...days.entries()].filter(([, count]) => count > 0).sort(([a], [b]) => (a < b ? -1 : 1))
  if (logged.length === 0) {
    return { total: 0, days: 0, streak: 0, longestStreak: 0, perDay: 0, busiest: null }
  }

  const total = logged.reduce((sum, [, count]) => sum + count, 0)
  const first = logged[0] as [DayKey, number]
  const span = Math.max(1, daysBetween(first[0], today) + 1)

  let longestStreak = 0
  let run = 0
  let previous: DayKey | null = null
  for (const [day] of logged) {
    run = previous !== null && daysBetween(previous, day) === 1 ? run + 1 : 1
    longestStreak = Math.max(longestStreak, run)
    previous = day
  }

  // A streak counting back from today, tolerating today itself being empty —
  // an unlogged today is a day still in progress, not a break.
  let streak = 0
  let cursor = days.get(today) ? today : shiftDayKey(today, -1)
  while ((days.get(cursor) ?? 0) > 0) {
    streak += 1
    cursor = shiftDayKey(cursor, -1)
  }

  const busiest = logged.reduce((best, [day, count]) =>
    count > best[1] ? [day, count] : best,
  )

  return {
    total,
    days: logged.length,
    streak,
    longestStreak,
    perDay: total / span,
    busiest: { day: busiest[0], count: busiest[1] },
  }
}

/**
 * The days of a heatmap, oldest first, padded so the first column starts on a
 * Monday. Null means "before the range", which draws as a hole rather than an
 * empty day — an empty square would claim nothing happened.
 */
export function heatmapDays(from: DayKey, to: DayKey): Array<DayKey | null> {
  const lead = (((new Date(`${from}T00:00:00Z`).getUTCDay() + 6) % 7) + 7) % 7
  const cells: Array<DayKey | null> = Array.from({ length: lead }, () => null)
  for (let day = from; day <= to; day = shiftDayKey(day, 1)) cells.push(day)
  return cells
}
